import { useState } from 'react';
import { api } from '../api/client';
import type { AuditEntry } from '../api/types';
import { useResource } from '../api/useResource';
import { Panel, Select, Spinner, Status } from '../components/ui';

const ENTITIES = ['', 'organ', 'disease', 'wheel', 'card', 'config', 'user'] as const;

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) return value.length > 6 ? `${value.length} items` : `[${value.join(', ')}]`;
  return String(value);
}

export function AuditPage() {
  const [entity, setEntity] = useState<string>('');
  const audit = useResource(
    () =>
      api<{ entries: AuditEntry[]; nextCursor: string | null }>(
        `/v1/admin/audit${entity ? `?entity=${entity}` : ''}`,
      ),
    [entity],
  );

  if (audit.loading && !audit.data) return <Spinner />;
  if (audit.error) return <Status error={audit.error} />;

  return (
    <Panel title="Audit" actions={<span className="muted">newest first</span>}>
      <p className="muted">
        Every admin write, with the fields that actually moved. The actor's email is a snapshot, so
        the trail survives that account being deleted.
      </p>

      <div className="row">
        <Select
          value={entity}
          options={ENTITIES.map((value) => ({ value, label: value || 'All entities' }))}
          onChange={setEntity}
        />
      </div>

      <table className="table">
        <thead>
          <tr>
            <th>When</th>
            <th>Who</th>
            <th>What</th>
            <th>Changes</th>
          </tr>
        </thead>
        <tbody>
          {audit.data?.entries.map((entry) => (
            <tr key={entry.id}>
              <td className="nowrap">{new Date(entry.createdAt).toLocaleString()}</td>
              <td>{entry.actorEmail}</td>
              <td>
                {entry.action} <code>{entry.entity}</code>
                {entry.entityId ? <> · <code>{entry.entityId}</code></> : null}
              </td>
              <td>
                {entry.changes
                  ? Object.entries(entry.changes).map(([field, change]) => (
                      <div key={field} className="change">
                        <code>{field}</code> {formatValue(change.from)} → {formatValue(change.to)}
                      </div>
                    ))
                  : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {audit.data?.entries.length === 0 ? <p className="muted">Nothing recorded yet.</p> : null}
    </Panel>
  );
}
